// The three things a person types on Cash Planning: a balance read off the
// banking app, a change to one date of a planned outflow, and a row of the plan
// itself. Every check that decides whether a value is allowed runs in the
// cashplan_* functions; these forms only collect it and show the refusal in the
// words the database used.
import { useEffect, useState } from 'react'
import { Button } from '../../lib/shadcn/button'
import { Input } from '../../lib/shadcn/input'
import { Label } from '../../lib/shadcn/label'
import { NativeSelect } from '../../lib/shadcn/native-select'
import { Textarea } from '../../lib/shadcn/textarea'
import { Checkbox } from '../../lib/shadcn/checkbox'
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from '../../lib/shadcn/dialog'
import { useClearCashPlanOverride, useEnterCashBalance, useSaveCashPlanOverride, useSaveCashPlanRow } from '../../hooks/backend/banking'
import {
  AMOUNT_RULE_LABEL, AMOUNT_RULES, BUSINESS_DAY_LABEL, CATEGORIES, CATEGORY_LABEL, WEEKDAYS,
  formatDay, formatZar, type ObligationItem, type ScheduleRow,
} from '../../utils/cashPlan'

function errorText(e: unknown): string {
  return e instanceof Error ? e.message : String(e)
}

function Refusal({ text }: { text: string | null }) {
  if (!text) return null
  return <p className="rounded-md border border-rose-200 bg-rose-50 px-3 py-2 text-sm text-rose-800 dark:border-rose-800 dark:bg-rose-950 dark:text-rose-200">{text}</p>
}

export function BalanceDialog({ open, onOpenChange, today, onSaved }: {
  open: boolean
  onOpenChange: (v: boolean) => void
  today: string
  onSaved: () => void
}) {
  const save = useEnterCashBalance<{ id: string }>()
  const [amount, setAmount] = useState('')
  const [asAt, setAsAt] = useState(today)
  const [note, setNote] = useState('')
  const [err, setErr] = useState<string | null>(null)
  useEffect(() => { if (open) { setAmount(''); setAsAt(today); setNote(''); setErr(null) } }, [open, today])

  const submit = async () => {
    setErr(null)
    try {
      await save.trigger({ amount_zar: amount, as_at: asAt, note })
      onOpenChange(false)
      onSaved()
    } catch (e) { setErr(errorText(e)) }
  }

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-md">
        <DialogHeader>
          <DialogTitle>Balance as at a date</DialogTitle>
          <DialogDescription>
            Read it off the banking app. It is used instead of the last statement until a newer statement is loaded.
          </DialogDescription>
        </DialogHeader>
        <div className="space-y-3">
          <div className="space-y-1">
            <Label htmlFor="cp-bal">Balance in rand (a minus for overdrawn)</Label>
            <Input id="cp-bal" inputMode="decimal" value={amount} onChange={(e) => setAmount(e.target.value)} placeholder="e.g. 48250.00" />
          </div>
          <div className="space-y-1">
            <Label htmlFor="cp-bal-date">As at the end of</Label>
            <Input id="cp-bal-date" type="date" max={today} value={asAt} onChange={(e) => setAsAt(e.target.value)} />
          </div>
          <div className="space-y-1">
            <Label htmlFor="cp-bal-note">Note (optional)</Label>
            <Input id="cp-bal-note" value={note} onChange={(e) => setNote(e.target.value)} placeholder="Where it was read" />
          </div>
          <Refusal text={err} />
          <div className="flex justify-end gap-2">
            <Button variant="outline" size="sm" onClick={() => onOpenChange(false)}>Cancel</Button>
            <Button variant="brand" size="sm" disabled={save.loading} onClick={() => void submit()}>Use this balance</Button>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  )
}

export function OverrideDialog({ item, date, onClose, onSaved }: {
  item: ObligationItem | null
  date: string | null
  onClose: () => void
  onSaved: () => void
}) {
  const save = useSaveCashPlanOverride<{ id: string }>()
  const clear = useClearCashPlanOverride<{ cleared: boolean }>()
  const [amount, setAmount] = useState('')
  const [moveTo, setMoveTo] = useState('')
  const [skip, setSkip] = useState(false)
  const [note, setNote] = useState('')
  const [err, setErr] = useState<string | null>(null)
  useEffect(() => {
    if (!item) return
    setAmount(item.override_id && item.amount_zar !== null ? String(item.amount_zar) : '')
    setMoveTo(item.moved && date ? date : '')
    setSkip(item.skipped)
    setNote(item.override_note ?? '')
    setErr(null)
  }, [item, date])

  if (!item) return null
  const submit = async () => {
    setErr(null)
    try {
      await save.trigger({ schedule_id: item.schedule_id, nominal_date: item.nominal_date, amount_zar: amount, move_to: moveTo, skip, note })
      onClose(); onSaved()
    } catch (e) { setErr(errorText(e)) }
  }
  const undo = async () => {
    setErr(null)
    try {
      await clear.trigger({ override_id: item.override_id })
      onClose(); onSaved()
    } catch (e) { setErr(errorText(e)) }
  }

  return (
    <Dialog open={!!item} onOpenChange={(v) => { if (!v) onClose() }}>
      <DialogContent className="max-w-md">
        <DialogHeader>
          <DialogTitle>{item.label}, {formatDay(date)}</DialogTitle>
          <DialogDescription>
            Change this one date only. The plan row keeps its rule for every other date.
          </DialogDescription>
        </DialogHeader>
        <div className="space-y-3 text-sm">
          <div className="rounded-md bg-muted px-3 py-2 text-xs text-muted-foreground">
            <p><span className="font-medium text-foreground">{item.known ? formatZar(item.amount_zar) : 'Amount unknown'}</span>{item.estimate ? ', an estimate' : ''}</p>
            {item.basis && <p className="mt-1">{item.basis}</p>}
            {!item.known && item.needs && <p className="mt-1">Needs: {item.needs}</p>}
          </div>
          <div className="space-y-1">
            <Label htmlFor="cp-ov-amt">The amount for this date</Label>
            <Input id="cp-ov-amt" inputMode="decimal" value={amount} onChange={(e) => setAmount(e.target.value)} placeholder="Leave empty to keep the rule's amount" />
          </div>
          <div className="space-y-1">
            <Label htmlFor="cp-ov-date">Move it to</Label>
            <Input id="cp-ov-date" type="date" value={moveTo} onChange={(e) => setMoveTo(e.target.value)} />
          </div>
          <label className="flex items-center gap-2 text-sm">
            <Checkbox checked={skip} onCheckedChange={(v) => setSkip(v === true)} /> Not paid this time: leave it out of the balance
          </label>
          <div className="space-y-1">
            <Label htmlFor="cp-ov-note">Why</Label>
            <Input id="cp-ov-note" value={note} onChange={(e) => setNote(e.target.value)} placeholder="e.g. SimplePay EMP201 for September" />
          </div>
          <Refusal text={err} />
          <div className="flex flex-wrap justify-end gap-2">
            {item.override_id && <Button variant="outline" size="sm" disabled={clear.loading} onClick={() => void undo()}>Back to the rule</Button>}
            <Button variant="outline" size="sm" onClick={onClose}>Cancel</Button>
            <Button variant="brand" size="sm" disabled={save.loading} onClick={() => void submit()}>Save for this date</Button>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  )
}

export type RowDraft = Partial<ScheduleRow> & { supplier: string }

const EMPTY: RowDraft = {
  supplier: '', category: 'other', rule_kind: 'day_of_month', day_of_month: 1, weekday: null, once_date: null,
  business_day_rule: 'next', amount_rule: 'last_debit', fixed_amount_zar: null, match_pattern: '', rule_key: null,
  source: '', active: true, starts_on: null, ends_on: null, notes: '',
}

export function RowDialog({ draft, onClose, onSaved }: {
  draft: RowDraft | null
  onClose: () => void
  onSaved: () => void
}) {
  const save = useSaveCashPlanRow<{ id: string }>()
  const [r, setR] = useState<RowDraft>(EMPTY)
  const [err, setErr] = useState<string | null>(null)
  useEffect(() => { if (draft) { setR({ ...EMPTY, ...draft }); setErr(null) } }, [draft])
  if (!draft) return null
  const set = <K extends keyof RowDraft>(k: K, v: RowDraft[K]) => setR((x) => ({ ...x, [k]: v }))

  const submit = async () => {
    setErr(null)
    try {
      await save.trigger({
        id: r.id ?? null, supplier: r.supplier, category: r.category, rule_kind: r.rule_kind,
        day_of_month: r.rule_kind === 'day_of_month' ? r.day_of_month : null,
        weekday: r.rule_kind === 'weekday' ? r.weekday : null,
        once_date: r.rule_kind === 'once' ? r.once_date : null,
        business_day_rule: r.business_day_rule, amount_rule: r.amount_rule,
        fixed_amount_zar: r.amount_rule === 'fixed' ? r.fixed_amount_zar : null,
        match_pattern: r.match_pattern, rule_key: r.rule_key, source: r.source, active: r.active,
        starts_on: r.starts_on, ends_on: r.ends_on, notes: r.notes, sort_order: r.sort_order ?? null,
      })
      onClose(); onSaved()
    } catch (e) { setErr(errorText(e)) }
  }

  return (
    <Dialog open={!!draft} onOpenChange={(v) => { if (!v) onClose() }}>
      <DialogContent className="max-h-[92vh] max-w-xl overflow-y-auto">
        <DialogHeader>
          <DialogTitle>{r.id ? `Change ${draft.supplier}` : 'Add to the plan'}</DialogTitle>
          <DialogDescription>
            When it falls, how its amount is found, and the words on the bank statement that recognise it.
          </DialogDescription>
        </DialogHeader>
        <div className="grid gap-3 text-sm sm:grid-cols-2">
          <div className="space-y-1 sm:col-span-2">
            <Label htmlFor="cp-r-sup">Supplier or item</Label>
            <Input id="cp-r-sup" value={r.supplier} onChange={(e) => set('supplier', e.target.value)} />
          </div>
          <div className="space-y-1">
            <Label htmlFor="cp-r-cat">Category</Label>
            <NativeSelect id="cp-r-cat" value={r.category} onChange={(e) => set('category', e.target.value as RowDraft['category'])}>
              {CATEGORIES.map((c) => <option key={c} value={c}>{CATEGORY_LABEL[c]}</option>)}
            </NativeSelect>
          </div>
          <div className="space-y-1">
            <Label htmlFor="cp-r-kind">When</Label>
            <NativeSelect id="cp-r-kind" value={r.rule_kind} onChange={(e) => set('rule_kind', e.target.value as RowDraft['rule_kind'])}>
              <option value="day_of_month">A day of each month</option>
              <option value="weekday">A day of each week</option>
              <option value="last_charge_day">Monthly, on the day it last charged</option>
              <option value="once">Once</option>
            </NativeSelect>
          </div>
          {r.rule_kind === 'day_of_month' && (
            <div className="space-y-1">
              <Label htmlFor="cp-r-dom">Day of the month</Label>
              <Input id="cp-r-dom" type="number" min={1} max={31} value={r.day_of_month ?? ''} onChange={(e) => set('day_of_month', e.target.value === '' ? null : Number(e.target.value))} />
            </div>
          )}
          {r.rule_kind === 'weekday' && (
            <div className="space-y-1">
              <Label htmlFor="cp-r-wd">Weekday</Label>
              <NativeSelect id="cp-r-wd" value={r.weekday ?? 2} onChange={(e) => set('weekday', Number(e.target.value))}>
                {WEEKDAYS.map((w, i) => <option key={w} value={i + 1}>{w}</option>)}
              </NativeSelect>
            </div>
          )}
          {r.rule_kind === 'once' && (
            <div className="space-y-1">
              <Label htmlFor="cp-r-once">Date</Label>
              <Input id="cp-r-once" type="date" value={r.once_date ?? ''} onChange={(e) => set('once_date', e.target.value || null)} />
            </div>
          )}
          <div className="space-y-1 sm:col-span-2">
            <Label htmlFor="cp-r-bd">If it lands on a weekend or public holiday</Label>
            <NativeSelect id="cp-r-bd" value={r.business_day_rule} onChange={(e) => set('business_day_rule', e.target.value as RowDraft['business_day_rule'])}>
              {(Object.keys(BUSINESS_DAY_LABEL) as (keyof typeof BUSINESS_DAY_LABEL)[]).map((k) => <option key={k} value={k}>{BUSINESS_DAY_LABEL[k]}</option>)}
            </NativeSelect>
          </div>
          <div className="space-y-1 sm:col-span-2">
            <Label htmlFor="cp-r-amt">Amount</Label>
            <NativeSelect id="cp-r-amt" value={r.amount_rule} onChange={(e) => set('amount_rule', e.target.value as RowDraft['amount_rule'])}>
              {AMOUNT_RULES.map((a) => <option key={a} value={a}>{AMOUNT_RULE_LABEL[a]}</option>)}
            </NativeSelect>
          </div>
          {r.amount_rule === 'fixed' && (
            <div className="space-y-1">
              <Label htmlFor="cp-r-fixed">Fixed amount in rand</Label>
              <Input id="cp-r-fixed" inputMode="decimal" value={r.fixed_amount_zar ?? ''} onChange={(e) => set('fixed_amount_zar', e.target.value === '' ? null : Number(e.target.value))} />
            </div>
          )}
          {r.amount_rule === 'insurer_bill_else_last_debit' && (
            <div className="space-y-1">
              <Label htmlFor="cp-r-key">Insurer, as named in insurer_monthly_bill</Label>
              <Input id="cp-r-key" value={r.rule_key ?? ''} onChange={(e) => set('rule_key', e.target.value)} />
            </div>
          )}
          <div className="space-y-1 sm:col-span-2">
            <Label htmlFor="cp-r-pat">Bank statement words</Label>
            <Input id="cp-r-pat" value={r.match_pattern ?? ''} onChange={(e) => set('match_pattern', e.target.value)} placeholder="e.g. Magtape Debit Cartrack" />
            <p className="text-xs text-muted-foreground">Matched against each line's description, ignoring case. Put a backslash before . * + ? ( ) [ ] and |.</p>
          </div>
          <div className="space-y-1 sm:col-span-2">
            <Label htmlFor="cp-r-src">Where the figure comes from</Label>
            <Input id="cp-r-src" value={r.source ?? ''} onChange={(e) => set('source', e.target.value)} />
          </div>
          <div className="space-y-1">
            <Label htmlFor="cp-r-from">From (optional)</Label>
            <Input id="cp-r-from" type="date" value={r.starts_on ?? ''} onChange={(e) => set('starts_on', e.target.value || null)} />
          </div>
          <div className="space-y-1">
            <Label htmlFor="cp-r-to">Until (optional)</Label>
            <Input id="cp-r-to" type="date" value={r.ends_on ?? ''} onChange={(e) => set('ends_on', e.target.value || null)} />
          </div>
          <div className="space-y-1 sm:col-span-2">
            <Label htmlFor="cp-r-notes">Notes</Label>
            <Textarea id="cp-r-notes" rows={3} value={r.notes ?? ''} onChange={(e) => set('notes', e.target.value)} />
          </div>
          <label className="flex items-center gap-2 sm:col-span-2">
            <Checkbox checked={r.active !== false} onCheckedChange={(v) => set('active', v === true)} /> In the plan (off keeps the row but plans nothing)
          </label>
          <div className="sm:col-span-2"><Refusal text={err} /></div>
          <div className="flex justify-end gap-2 sm:col-span-2">
            <Button variant="outline" size="sm" onClick={onClose}>Cancel</Button>
            <Button variant="brand" size="sm" disabled={save.loading} onClick={() => void submit()}>Save</Button>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  )
}
